import { getProcessedModelSchemaForPostSample } from "../graphql/customQueries";
import { getTargetDataset, getTemplate, listDatasetLinks, listOutputLinks, parseJson, query } from "./historicalModelService";
import {
  buildDatasetInputSample,
  buildExecutionPostSample,
  buildHistoricalPostSample,
  buildModelOutputInputSample,
  forecastInputConfigOf,
  forecastInputWindow,
  sampleHistoricalWindow,
} from "../utils/processedModelPostSample";
import type { ExecutionPostSample, HistoricalPostSample } from "../utils/processedModelPostSample";
import { effectiveConfig, inputWindow } from "../utils/historicalRuns";
import type { DatasetFileRow, ForecastRecordRow, HistoricalRunConfig, TimeWindow } from "../utils/historicalRuns";
import { safeFileSegment } from "../utils/modelsList";

// The sample POST body for a model (utils/processedModelPostSample.ts): the reads its run would make now, without
// presigning. Admin only in the UI: DatasetFile is readable by the Admin group alone.

// A month file covers at most 31 days (historicalModelRunFunction/src/datasets.ts LONGEST_PERIOD_MS)
const LONGEST_PERIOD_MS = 31 * 86_400_000;

const DATASET_FILES = /* GraphQL */ `
  query PostSampleDatasetFiles($datasetId: ID!, $start: String!, $end: String!, $nextToken: String) {
    datasetFilesByDatasetIdAndPeriodStart(
      datasetId: $datasetId
      periodStart: { between: [$start, $end] }
      filter: { active: { eq: true } }
      limit: 500
      nextToken: $nextToken
    ) {
      items { id s3Key granularity periodStart periodEnd version active }
      nextToken
    }
  }
`;

const GET_OUTPUT = /* GraphQL */ `
  query PostSampleOutput($id: ID!) {
    getProcessedModelOutputParam(id: $id) { id unit outputLocation schemaId active schema { id s3BasePath } }
  }
`;

const FORECAST_RECORDS = /* GraphQL */ `
  query PostSampleRecords($parameterId: ID!, $forecastIssued: ModelStringKeyConditionInput, $limit: Int, $sortDirection: ModelSortDirection, $nextToken: String) {
    forecastRecordsByParameterIdAndForecastIssued(
      parameterId: $parameterId
      forecastIssued: $forecastIssued
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items { id s3Key forecastIssued seriesStart seriesEnd }
      nextToken
    }
  }
`;

/** Every page of a connection query. */
async function allPages<T>(document: string, variables: Record<string, unknown>, field: string, fallback: string): Promise<T[]> {
  const items: T[] = [];
  let nextToken: string | null = null;
  do {
    const data: any = await query(document, { ...variables, nextToken }, fallback);
    items.push(...(data?.[field]?.items || []).filter(Boolean));
    nextToken = data?.[field]?.nextToken || null;
  } while (nextToken);
  return items;
}

const datasetFileRows = (datasetId: string, reach: TimeWindow) =>
  allPages<DatasetFileRow>(
    DATASET_FILES,
    { datasetId, start: new Date(Date.parse(reach.start) - LONGEST_PERIOD_MS).toISOString(), end: reach.end },
    "datasetFilesByDatasetIdAndPeriodStart",
    "Could not load the dataset's files."
  );

/** An output's issues made inside the window (HISTORICAL), or its latest N (FORECAST). */
async function forecastRecords(parameterId: string, pick: { window: TimeWindow } | { latest: number }): Promise<ForecastRecordRow[]> {
  const field = "forecastRecordsByParameterIdAndForecastIssued";
  if ("latest" in pick) {
    const data: any = await query(FORECAST_RECORDS, { parameterId, sortDirection: "DESC", limit: pick.latest }, "Could not load the output's issues.");
    return (data?.[field]?.items || []).filter(Boolean);
  }
  return allPages<ForecastRecordRow>(
    FORECAST_RECORDS,
    { parameterId, forecastIssued: { between: [pick.window.start, pick.window.end] }, limit: 500 },
    field,
    "Could not load the output's issues."
  );
}

/** The model_output inputs, each as its kind's run function builds it. A link that can't be read is noted and left out. */
async function modelOutputInputs(
  modelId: string,
  shapeOf: (fieldKey: string) => { window: TimeWindow; pick: { window: TimeWindow } | { latest: number } },
  notes: string[]
): Promise<Record<string, unknown>> {
  const inputs: Record<string, unknown> = {};
  for (const link of await listOutputLinks(modelId)) {
    try {
      const output = ((await query(GET_OUTPUT, { id: link.outputParamId }, "Could not load an upstream output.")) as any)?.getProcessedModelOutputParam;
      if (!output || output.active === false) throw new Error("the output no longer exists");
      const { window, pick } = shapeOf(link.fieldKey);
      inputs[link.fieldKey] = buildModelOutputInputSample({
        link,
        output,
        source: output.schema ?? { id: output.schemaId || link.sourceModelId, s3BasePath: null },
        records: await forecastRecords(output.id, pick),
        window,
        pick,
      });
    } catch (err: any) {
      notes.push(`Input "${link.fieldKey}" left out: ${err?.message || err}`);
    }
  }
  return inputs;
}

export interface PostSampleFile {
  fileName: string;
  /** `_sampleNotes` is set only when an input couldn't be read: the sample is then partial. It isn't part of the real body. */
  body: (ExecutionPostSample | HistoricalPostSample) & { _sampleNotes?: string[] };
}

/** The POST body a run of this model would send its executionUrl now (placeholders for run-time values). */
export async function buildPostSampleForModel(modelId: string, now: Date = new Date()): Promise<PostSampleFile> {
  const schema = ((await query(getProcessedModelSchemaForPostSample, { id: modelId }, "Could not load the model.")) as any)?.getProcessedModelSchema;
  if (!schema) throw new Error("Could not load the model: not found");
  const template = await getTemplate(schema.schemaType);
  const fileName = `postsample_${safeFileSegment(schema.name)}_${schema.id}.json`;
  const notes: string[] = [];
  const withNotes = (body: ExecutionPostSample | HistoricalPostSample): PostSampleFile => ({
    fileName,
    body: notes.length ? { ...body, _sampleNotes: notes } : body,
  });

  if (schema.modelKind !== "HISTORICAL") {
    const inputs = await modelOutputInputs(
      schema.id,
      (fieldKey) => {
        const { lookback, latestIssues } = forecastInputConfigOf(template?.fieldDefinitions ?? {}, fieldKey);
        return { window: forecastInputWindow(lookback, now), pick: { latest: latestIssues } };
      },
      notes
    );
    return withNotes(buildExecutionPostSample(schema, inputs));
  }

  const config = effectiveConfig(
    (template?.historicalConfig ?? {}) as HistoricalRunConfig,
    parseJson<HistoricalRunConfig | null>(schema.historicalSettings, null)
  );
  const window = sampleHistoricalWindow(config, now);
  const reach = inputWindow(window, config);

  const inputs: Record<string, unknown> = {};
  for (const link of await listDatasetLinks(schema.id)) {
    try {
      const dataset = await getTargetDataset(link.datasetId);
      if (!dataset) throw new Error("the dataset no longer exists");
      inputs[link.fieldKey] = buildDatasetInputSample({ link, dataset, fileRows: await datasetFileRows(dataset.id, reach), reach });
    } catch (err: any) {
      notes.push(`Input "${link.fieldKey}" left out: ${err?.message || err}`);
    }
  }
  Object.assign(inputs, await modelOutputInputs(schema.id, () => ({ window: reach, pick: { window: reach } }), notes));

  const targetRoutes: Record<string, string | null> = {};
  for (const output of schema.outputs?.items || []) {
    if (output?.targetDatasetId) targetRoutes[output.id] = (await getTargetDataset(output.targetDatasetId).catch(() => null))?.routeLong ?? null;
  }
  return withNotes(buildHistoricalPostSample({ schema, window, inputs, targetRoutes, now }));
}
